"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import useSWR from "swr";
import { isSellerPriceBandRecommendationChipsOperational } from "@/lib/pricing/pricingInsightSettingsPolicy";
import {
  pricingInsightFlagsFetcher,
  type PricingInsightFeatureFlags,
  PRICING_INSIGHT_FLAGS_CACHE_TTL_MS,
  PRICING_INSIGHT_FLAGS_SWR_KEY,
} from "@/lib/pricingInsightFlagsClient";

type PricingInsightFlagsContextValue = {
  flags: PricingInsightFeatureFlags | null;
  isLoading: boolean;
  error: string | null;
  /** global && user_pricing_insight_enabled */
  isUserPricingInsightEnabled: boolean;
  /** global && pricing_insight_enabled */
  isAdminPricingInsightEnabled: boolean;
  /** system_settings.seller_price_band_enforcement_enabled (Admin → Settings) */
  isSellerPriceBandEnforcementEnabled: boolean;
  /** Admin setting: clickable mid / quick-sale price chips on /sell. */
  isSellerPriceBandRecommendationChipsEnabled: boolean;
  refresh: (force?: boolean) => Promise<void>;
};

const PricingInsightFlagsContext =
  createContext<PricingInsightFlagsContextValue | null>(null);

function deriveUserEnabled(flags: PricingInsightFeatureFlags | null): boolean {
  if (!flags) return false;
  return (
    flags.global_pricing_insight_enabled && flags.user_pricing_insight_enabled
  );
}

function deriveAdminEnabled(flags: PricingInsightFeatureFlags | null): boolean {
  if (!flags) return false;
  return flags.global_pricing_insight_enabled && flags.pricing_insight_enabled;
}

export function PricingInsightFlagsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { data, error, isLoading, mutate } = useSWR<
    PricingInsightFeatureFlags,
    Error
  >(PRICING_INSIGHT_FLAGS_SWR_KEY, pricingInsightFlagsFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: PRICING_INSIGHT_FLAGS_CACHE_TTL_MS,
    refreshInterval: PRICING_INSIGHT_FLAGS_CACHE_TTL_MS,
    refreshWhenHidden: false,
  });

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void mutate();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [mutate]);

  const flags = data ?? null;
  const errorMessage =
    error instanceof Error ? error.message : error ? String(error) : null;

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const value = useMemo<PricingInsightFlagsContextValue>(
    () => ({
      flags,
      isLoading,
      error: errorMessage,
      isUserPricingInsightEnabled: deriveUserEnabled(flags),
      isAdminPricingInsightEnabled: deriveAdminEnabled(flags),
      isSellerPriceBandEnforcementEnabled:
        Boolean(flags?.seller_price_band_enforcement_enabled) &&
        deriveUserEnabled(flags),
      isSellerPriceBandRecommendationChipsEnabled: flags
        ? isSellerPriceBandRecommendationChipsOperational({
            global_pricing_insight_enabled:
              flags.global_pricing_insight_enabled,
            user_pricing_insight_enabled: flags.user_pricing_insight_enabled,
            seller_price_band_enforcement_enabled:
              flags.seller_price_band_enforcement_enabled,
            seller_price_band_recommendation_chips_enabled:
              flags.seller_price_band_recommendation_chips_enabled,
          })
        : false,
      refresh,
    }),
    [flags, isLoading, errorMessage, refresh],
  );

  return (
    <PricingInsightFlagsContext.Provider value={value}>
      {children}
    </PricingInsightFlagsContext.Provider>
  );
}

export function usePricingInsightFlagsContext(): PricingInsightFlagsContextValue {
  const ctx = useContext(PricingInsightFlagsContext);
  if (!ctx) {
    throw new Error(
      "usePricingInsightFlagsContext must be used within PricingInsightFlagsProvider",
    );
  }
  return ctx;
}
